import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, FormField } from "@components/input";
import { useLocale } from "@hooks/useLocale";
import { useAppDispatch } from "@app/hooks";
import { selectWell, setActiveEvent } from "@app/slices/wellContextSlice";
import { useGetWellsQuery, useUpsertWellMutation } from "@api/mApi";
import { useCreateEventMutation } from "@api/pApi";
import type { WellOut } from "@api/types";

/**
 * The event kinds the wizard can create. "workover" is the original MVP;
 * "drilling" reuses the same Pozo step with a lighter event step (no
 * reacondicionamiento Nro, no AFE; adds an optional Taladro/rig).
 * "completion" and "intervention" reuse the same Pozo step and an event step
 * with objetivo/fecha/dias + AFE Nro (-> source_event_id) + a sequence Nro
 * that forms the event_code ("COMP"+n / "INT"+n). NO reacondicionamiento Nro.
 */
export type CreateEventType = "workover" | "drilling" | "completion" | "intervention";

/** The i18n namespace per event kind — kept as a literal union so `${ns}.title`
 *  et al. resolve to a MessageKey at the call site. */
type EventNs = "createwo" | "createdr" | "createcomp" | "createint";

/** Per-type i18n namespace + event_code prefix for the sequence Nro. */
const TYPE_CONFIG: Record<CreateEventType, { ns: EventNs; codePrefix: string }> = {
  workover: { ns: "createwo", codePrefix: "WO" },
  drilling: { ns: "createdr", codePrefix: "" },
  completion: { ns: "createcomp", codePrefix: "COMP" },
  intervention: { ns: "createint", codePrefix: "INT" },
};

export interface CreateWorkoverModalProps {
  open: boolean;
  onClose: () => void;
  /** Which event kind to create. Defaults to "workover" (the original MVP). */
  eventType?: CreateEventType;
}

type Step = "well" | "event";
type WellMode = "existing" | "new";

/**
 * Crear Workover / Crear Drilling — the admin-only wizard that creates (or
 * re-points at) a well asset and then creates an event on it, landing the user
 * on the new event's Parte diario. Parameterized by `eventType`:
 *
 *   1. Pozo — pick an existing well from the register, OR "Nuevo pozo" to
 *      upsert a fresh asset (uwi + common_name required; field/operator
 *      optional). The upsert is idempotent on (org, uwi) so it never duplicates
 *      ENO-8. (Identical for both event kinds.)
 *   2. Evento —
 *        - workover: objetivo, fecha de inicio, dias plan, Nro de
 *          reacondicionamiento (-> event_code "WO"+n) and AFE Nro
 *          (-> source_event_id).
 *        - drilling: objetivo, fecha de inicio, dias plan, Taladro (optional,
 *          -> event_code). NO reacondicionamiento Nro, NO AFE.
 *
 * Submit: (if new well) PUT /v1/wells -> then POST /v1/events {event_type, ...}.
 * On the 201 it selects the well + the new event in wellContext and navigates
 * to /daily. Required fields are validated before a submit is allowed; both
 * mutations' pending state disables the primary action; errors render inline.
 */
export function CreateWorkoverModal({
  open,
  onClose,
  eventType = "workover",
}: CreateWorkoverModalProps) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isDrilling = eventType === "drilling";
  // workover shows the "Nro de reacondicionamiento" label; completion/
  // intervention reuse the same sequence field under a type-specific label.
  const isWorkover = eventType === "workover";
  const { ns, codePrefix } = TYPE_CONFIG[eventType];

  // Sequence-Nro label: workover keeps "Nro de reacondicionamiento"; completion
  // and intervention use their own `.seqNro` key. (drilling never shows it.)
  const seqNroLabel = isWorkover
    ? t("createwo.wo.reacondNro")
    : eventType === "completion"
      ? t("createcomp.seqNro")
      : t("createint.seqNro");

  const { data: wells = [] } = useGetWellsQuery();
  const [upsertWell, { isLoading: upserting }] = useUpsertWellMutation();
  const [createEvent, { isLoading: creating }] = useCreateEventMutation();

  const [step, setStep] = useState<Step>("well");
  const [wellMode, setWellMode] = useState<WellMode>("existing");

  // Step 1 — well
  const [selectedWellId, setSelectedWellId] = useState("");
  const [wellSearch, setWellSearch] = useState("");
  const [newUwi, setNewUwi] = useState("");
  const [newCommonName, setNewCommonName] = useState("");
  const [newField, setNewField] = useState("");
  const [newOperator, setNewOperator] = useState("");

  // Step 2 — event
  const [objective, setObjective] = useState("");
  const [dateStart, setDateStart] = useState("");
  const [planDays, setPlanDays] = useState("");
  const [reacondNro, setReacondNro] = useState("");
  const [afeNro, setAfeNro] = useState("");
  const [rig, setRig] = useState("");

  const [submitError, setSubmitError] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // Reset the whole wizard whenever it (re)opens so a prior draft never leaks in.
  useEffect(() => {
    if (!open) return;
    setStep("well");
    setWellMode("existing");
    setSelectedWellId("");
    setWellSearch("");
    setNewUwi("");
    setNewCommonName("");
    setNewField("");
    setNewOperator("");
    setObjective("");
    setDateStart("");
    setPlanDays("");
    setReacondNro("");
    setAfeNro("");
    setRig("");
    setSubmitError(false);
    setShowErrors(false);
  }, [open]);

  const filteredWells = useMemo(() => {
    const q = wellSearch.trim().toLowerCase();
    const sorted = [...wells].sort((a, b) => a.commonName.localeCompare(b.commonName));
    if (!q) return sorted;
    return sorted.filter((w) =>
      `${w.commonName} ${w.uwi} ${w.apiNo ?? ""} ${w.field}`.toLowerCase().includes(q),
    );
  }, [wells, wellSearch]);

  const wellStepValid =
    wellMode === "existing"
      ? selectedWellId.trim().length > 0
      : newUwi.trim().length > 0 && newCommonName.trim().length > 0;

  // All kinds need objetivo + fecha. workover/completion/intervention also need
  // the sequence Nro (reused `reacondNro` state); drilling does not.
  const eventStepValid =
    objective.trim().length > 0 &&
    dateStart.trim().length > 0 &&
    (isDrilling || reacondNro.trim().length > 0);

  const pending = upserting || creating;

  const goNext = () => {
    setShowErrors(true);
    if (!wellStepValid) return;
    setShowErrors(false);
    setStep("event");
  };

  const goBack = () => {
    setShowErrors(false);
    setStep("well");
  };

  const submit = async () => {
    setShowErrors(true);
    setSubmitError(false);
    if (!wellStepValid || !eventStepValid) return;

    try {
      // 1. Resolve the well UWI + the M well id we must select afterwards.
      let wellUwi: string;
      let wellId: string;

      if (wellMode === "new") {
        const uwi = newUwi.trim();
        const result = await upsertWell({
          uwi,
          common_name: newCommonName.trim(),
          field: newField.trim() || null,
          operator: newOperator.trim() || null,
        }).unwrap();
        wellUwi = uwi;
        // The result envelope may be a bare WellOut or a { well } wrapper; also
        // fall back to the refreshed register (invalidated by the upsert) so we
        // always recover the M well id needed by selectWell.
        const w: Partial<WellOut> = result?.well ?? (result as Partial<WellOut>);
        wellId = w?.id ?? result?.id ?? "";
      } else {
        const chosen = wells.find((w) => w.id === selectedWellId);
        if (!chosen) {
          setSubmitError(true);
          return;
        }
        wellUwi = chosen.uwi;
        wellId = chosen.id;
      }

      // 2. Create the event.
      //    workover            -> event_code "WO"+n   + AFE Nro (source_event_id)
      //    completion          -> event_code "COMP"+n + AFE Nro (source_event_id)
      //    intervention        -> event_code "INT"+n  + AFE Nro (source_event_id)
      //    drilling            -> optional Taladro in event_code, no AFE.
      const days = planDays.trim();
      const eventCode = isDrilling
        ? rig.trim() || null
        : `${codePrefix}${reacondNro.trim()}`;
      const created = await createEvent({
        well_uwi: wellUwi,
        event_type: eventType,
        event_code: eventCode,
        source_event_id: isDrilling ? null : afeNro.trim() || null,
        objective_1: objective.trim(),
        date_ops_start: dateStart,
        estimated_days: days ? Number(days) : null,
      }).unwrap();

      // 3. For a brand-new well the id may not have come back on the envelope;
      // recover it from the refetched register by uwi before selecting.
      if (!wellId) {
        const refreshed = wells.find((w) => w.uwi === wellUwi);
        wellId = refreshed?.id ?? created.well_uwi;
      }

      dispatch(selectWell(wellId));
      dispatch(setActiveEvent(created.id));
      onClose();
      navigate("/daily");
    } catch {
      setSubmitError(true);
    }
  };

  const footer =
    step === "well" ? (
      <>
        <button type="button" className="btn" onClick={onClose} disabled={pending}>
          {t("createwo.cancel")}
        </button>
        <button type="button" className="btn primary" onClick={goNext}>
          {t("createwo.next")}
        </button>
      </>
    ) : (
      <>
        <button type="button" className="btn" onClick={goBack} disabled={pending}>
          {t("createwo.back")}
        </button>
        <button
          type="button"
          className="btn primary"
          onClick={() => void submit()}
          disabled={pending || !eventStepValid}
        >
          {pending ? t("createwo.submitting") : t(`${ns}.submit`)}
        </button>
      </>
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(`${ns}.title`)}
      context={step === "well" ? t("createwo.step.well") : t(`${ns}.step.event`)}
      width={620}
      footer={footer}
      footerNote={submitError ? <span className="err">{t(`${ns}.err.submit`)}</span> : null}
    >
      {step === "well" ? (
        <div className="mform">
          <FormField label={t("createwo.step.well")} owner="m">
            <div role="radiogroup" aria-label={t("createwo.step.well")}>
              <label style={{ marginRight: 16 }}>
                <input
                  type="radio"
                  name="cwo-wellmode"
                  checked={wellMode === "existing"}
                  onChange={() => setWellMode("existing")}
                />{" "}
                {t("createwo.well.existing")}
              </label>
              <label>
                <input
                  type="radio"
                  name="cwo-wellmode"
                  checked={wellMode === "new"}
                  onChange={() => setWellMode("new")}
                />{" "}
                {t("createwo.well.new")}
              </label>
            </div>
          </FormField>

          {wellMode === "existing" ? (
            <FormField
              label={t("createwo.well.select")}
              required
              owner="m"
              error={showErrors && !wellStepValid ? t("createwo.err.wellRequired") : undefined}
            >
              <input
                type="search"
                placeholder={t("explorer.search")}
                value={wellSearch}
                onChange={(e) => setWellSearch(e.target.value)}
                aria-label={t("explorer.search")}
                style={{ marginBottom: 6 }}
              />
              <select
                value={selectedWellId}
                onChange={(e) => setSelectedWellId(e.target.value)}
                size={6}
                aria-label={t("createwo.well.select")}
              >
                <option value="" disabled>
                  {t("createwo.well.selectPlaceholder")}
                </option>
                {filteredWells.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.commonName} — {w.uwi}
                  </option>
                ))}
              </select>
            </FormField>
          ) : (
            <>
              <FormField
                label={t("createwo.well.uwi")}
                required
                owner="m"
                error={
                  showErrors && newUwi.trim().length === 0 ? t("createwo.err.required") : undefined
                }
              >
                <input
                  type="text"
                  aria-label={t("createwo.well.uwi")}
                  value={newUwi}
                  onChange={(e) => setNewUwi(e.target.value)}
                />
              </FormField>
              <FormField
                label={t("createwo.well.commonName")}
                required
                owner="m"
                error={
                  showErrors && newCommonName.trim().length === 0
                    ? t("createwo.err.required")
                    : undefined
                }
              >
                <input
                  type="text"
                  aria-label={t("createwo.well.commonName")}
                  value={newCommonName}
                  onChange={(e) => setNewCommonName(e.target.value)}
                />
              </FormField>
              <FormField label={t("createwo.well.field")} owner="m">
                <input
                  type="text"
                  aria-label={t("createwo.well.field")}
                  value={newField}
                  onChange={(e) => setNewField(e.target.value)}
                />
              </FormField>
              <FormField label={t("createwo.well.operator")} owner="m">
                <input
                  type="text"
                  aria-label={t("createwo.well.operator")}
                  value={newOperator}
                  onChange={(e) => setNewOperator(e.target.value)}
                />
              </FormField>
            </>
          )}
        </div>
      ) : (
        <div className="mform">
          <FormField
            label={t("createwo.wo.objective")}
            required
            owner="p"
            error={
              showErrors && objective.trim().length === 0 ? t("createwo.err.required") : undefined
            }
          >
            <textarea
              className="text-area"
              rows={3}
              aria-label={t("createwo.wo.objective")}
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
            />
          </FormField>
          <FormField
            label={t("createwo.wo.dateStart")}
            required
            owner="p"
            error={
              showErrors && dateStart.trim().length === 0 ? t("createwo.err.required") : undefined
            }
          >
            <input
              type="date"
              aria-label={t("createwo.wo.dateStart")}
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
            />
          </FormField>
          <FormField label={t("createwo.wo.planDays")} owner="p">
            <input
              type="number"
              min={0}
              aria-label={t("createwo.wo.planDays")}
              value={planDays}
              onChange={(e) => setPlanDays(e.target.value)}
            />
          </FormField>
          {isDrilling ? (
            <FormField label={t("createdr.dr.rig")} owner="p">
              <input
                type="text"
                aria-label={t("createdr.dr.rig")}
                value={rig}
                onChange={(e) => setRig(e.target.value)}
              />
            </FormField>
          ) : (
            <>
              <FormField
                label={seqNroLabel}
                required
                owner="p"
                error={
                  showErrors && reacondNro.trim().length === 0
                    ? t("createwo.err.required")
                    : undefined
                }
              >
                <input
                  type="number"
                  min={0}
                  aria-label={seqNroLabel}
                  value={reacondNro}
                  onChange={(e) => setReacondNro(e.target.value)}
                />
              </FormField>
              <FormField label={t("createwo.wo.afeNro")} owner="p">
                <input
                  type="text"
                  aria-label={t("createwo.wo.afeNro")}
                  value={afeNro}
                  onChange={(e) => setAfeNro(e.target.value)}
                />
              </FormField>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
